import React from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiTrendingUp, FiShield, FiZap, FiActivity, FiCheckCircle, FiTarget } = FiIcons;

const Impact = ({ viewMode }) => {
  // Chart 1: Security Posture Improvement (Radar Chart)
  const securityRadarOption = {
    title: {
      text: 'Security Maturity Model',
      left: 'center',
      textStyle: { color: '#374151', fontSize: 16 }
    },
    tooltip: {},
    radar: {
      indicator: [
        { name: 'Compliance (SOC2/PCI)', max: 100 },
        { name: 'Identity (MFA/SSO)', max: 100 },
        { name: 'Infrastructure Uptime', max: 100 },
        { name: 'Data Protection (DLP)', max: 100 },
        { name: 'Cloud Security', max: 100 },
        { name: 'Incident Response', max: 100 }
      ],
      splitArea: {
        areaStyle: {
          color: ['#f8fafc', '#f1f5f9', '#e2e8f0', '#cbd5e1']
        }
      }
    },
    series: [{
      name: 'Impact Metrics',
      type: 'radar',
      data: [
        {
          value: [95, 100, 99.9, 92, 94, 93],
          name: 'Current Professional Standing',
          itemStyle: { color: '#0d9488' },
          areaStyle: { opacity: 0.3 }
        }
      ]
    }]
  };

  // Chart 2: Migration Success & Scale (Bar Chart)
  const migrationOption = {
    title: {
      text: 'Migration Scale & Success',
      left: 'center',
      textStyle: { color: '#374151', fontSize: 16 }
    },
    tooltip: { trigger: 'axis' },
    xAxis: {
      type: 'category',
      data: ['Acquisitions', 'Cloud Tenants', 'MFA Rollout', 'Infrastructure', 'SaaS Tools'],
      axisLabel: { color: '#6b7280' }
    },
    yAxis: { type: 'value', axisLabel: { color: '#6b7280' } },
    series: [{
      data: [12, 8, 100, 45, 25],
      type: 'bar',
      showBackground: true,
      backgroundStyle: { color: 'rgba(180, 180, 180, 0.1)' },
      itemStyle: {
        color: '#0d9488',
        borderRadius: [4, 4, 0, 0]
      }
    }]
  };

  const highLevelMetrics = [
    { label: "MFA Adoption", value: "100%", icon: FiShield, color: "text-teal-600", bg: "bg-teal-100" },
    { label: "System Uptime", value: "99.99%", icon: FiActivity, color: "text-blue-600", bg: "bg-blue-100" },
    { label: "Audit Pass Rate", value: "100%", icon: FiCheckCircle, color: "text-green-600", bg: "bg-green-100" },
    { label: "Compliance Score", value: "98/100", icon: FiTarget, color: "text-purple-600", bg: "bg-purple-100" }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      {/* Hero Header */}
      <section className="section-padding bg-gradient-to-br from-teal-800 to-blue-900 text-white">
        <motion.div 
          className="container mx-auto text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex justify-center mb-6">
            <SafeIcon icon={FiTrendingUp} className="w-16 h-16 text-yellow-400" />
          </div>
          <h1 className="font-heading text-4xl md:text-6xl font-bold mb-6">Quantifiable Impact</h1>
          <p className="text-xl text-teal-100 max-w-2xl mx-auto leading-relaxed">
            Visualizing the architecture, security, and compliance milestones achieved over three decades of technical leadership.
          </p>
        </motion.div>
      </section>

      {/* Metrics Grid */}
      <section className="py-12 -mt-16 relative z-10">
        <div className="container mx-auto">
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {highLevelMetrics.map((metric, idx) => (
              <motion.div 
                key={idx} 
                variants={itemVariants}
                className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 flex items-center space-x-4"
                whileHover={{ y: -5, scale: 1.02 }}
              >
                <div className={`w-14 h-14 ${metric.bg} rounded-xl flex items-center justify-center`}>
                  <SafeIcon icon={metric.icon} className={`w-7 h-7 ${metric.color}`} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-500 uppercase tracking-wider">{metric.label}</div>
                  <div className="text-2xl font-bold text-gray-900">{metric.value}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Visualizations Section */}
      <section className="section-padding">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Radar Chart Card */}
            <motion.div 
              className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="font-heading text-2xl font-bold mb-6 text-gray-900 flex items-center">
                <SafeIcon icon={FiShield} className="mr-3 text-teal-600" />
                Security Maturity Matrix
              </h3>
              <div className="h-[400px]">
                <ReactECharts option={securityRadarOption} style={{ height: '100%' }} />
              </div>
              <p className="mt-6 text-gray-600 text-sm italic">
                * Based on internal audits across M365 ecosystems and infrastructure management cycles.
              </p>
            </motion.div>

            {/* Bar Chart Card */}
            <motion.div 
              className="bg-white p-8 rounded-3xl shadow-lg border border-gray-100"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h3 className="font-heading text-2xl font-bold mb-6 text-gray-900 flex items-center">
                <SafeIcon icon={FiZap} className="mr-3 text-blue-600" />
                Operational Efficiency
              </h3>
              <div className="h-[400px]">
                <ReactECharts option={migrationOption} style={{ height: '100%' }} />
              </div>
              <p className="mt-6 text-gray-600 text-sm italic">
                * Figures represent historical project success rates and adoption metrics.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="section-padding bg-gray-900 text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 opacity-10">
          <SafeIcon icon={FiActivity} className="w-96 h-96 -mr-20 -mt-20" />
        </div>
        <motion.div 
          className="container mx-auto text-center relative z-10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <h2 className="text-3xl md:text-5xl font-heading font-bold mb-8 italic">
            "The goal of security is not to hinder progress, but to enable it with confidence."
          </h2>
          <div className="h-1 w-24 bg-teal-500 mx-auto mb-8"></div>
          <p className="text-xl text-gray-400 font-medium">Gary Fagerholm — Security IT Engineer</p>
        </motion.div>
      </section>
    </div>
  );
};

export default Impact;